import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { FiArrowLeft } from 'react-icons/fi';
import { adminAPI } from '../../../services/api';
import { formatDate } from '../../../utils/formatters';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorAlert from '../../../components/ErrorAlert';

const TYPE_LABELS = {
  receipt_void: 'Receipt Void',
  quantity_adjustment: 'Quantity Adjustment',
  warehouse_transfer: 'Warehouse Transfer'
};

const Field = ({ label, children }) => (
  <div>
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900">{children || '—'}</dd>
  </div>
);

const CorrectionDetail = () => {
  const { id } = useParams();

  const { data: correction, isLoading, isError, error } = useQuery(
    ['admin-correction', id],
    () => adminAPI.getCorrection(id),
    { enabled: !!id, refetchOnWindowFocus: false }
  );

  if (isLoading) return <div className="p-8"><LoadingSpinner /></div>;
  if (isError) return <ErrorAlert message={error?.message || 'Failed to load correction'} />;
  if (!correction) return <ErrorAlert message="Correction not found" />;

  const lines = correction.Lines || [];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <Link to="/admin/corrections" className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800 mb-4">
        <FiArrowLeft className="mr-1 h-4 w-4" /> Back to corrections
      </Link>

      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6 flex items-start justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{correction.correction_number}</h1>
            <p className="mt-1 text-sm text-gray-500">
              {TYPE_LABELS[correction.correction_type] || correction.correction_type}
              {' · '}{formatDate(correction.created_at || correction.createdAt)}
            </p>
          </div>
          {correction.requires_billing_review ? (
            <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
              Needs billing review
            </span>
          ) : (
            <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              Billing handled
            </span>
          )}
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-5">
            <Field label="Created by">{correction.CreatedBy?.name}</Field>
            <Field label="Reason code">{correction.reason_code}</Field>
            <Field label="Source request">
              {correction.source_request_id ? (
                <Link to={`/admin/requests/${correction.source_request_id}`} className="text-indigo-600 hover:text-indigo-800">
                  View request
                </Link>
              ) : null}
            </Field>
            <Field label="Customer-facing reason">{correction.customer_reason}</Field>
            <Field label="Internal notes">{correction.internal_notes}</Field>
          </dl>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Correction Lines</h2>
        </div>
        {lines.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">No lines recorded.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qty Change</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Warehouse</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {lines.map((line) => (
                  <tr key={line.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{line.InventoryItem?.product_sku || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {line.InventoryItem?.product_name || '—'}
                      {line.stock_only && (
                        <span
                          className="ml-2 inline-flex px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                          title="Stock count reconciled only — no physical units were changed"
                        >
                          stock only
                        </span>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-sm text-right font-medium ${line.quantity_delta < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {line.quantity_delta > 0 ? `+${line.quantity_delta}` : line.quantity_delta}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {line.FromWarehouse?.name || line.ToWarehouse?.name || '—'}
                      {line.FromWarehouse && line.ToWarehouse ? ` → ${line.ToWarehouse.name}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CorrectionDetail;
